<template>
	<div v-if="isSelectionOpen">
		<v-card class="d-flex align-center flex-column fill-height" style="background-color: black">
			<v-icon class="d-flex flex-column ma-3" icon="mdi-earth" size="80" color="white"/>
			<span class="d-flex flex-column font-weight-bold text-white"> Currently Using {{ selectedStationName }} Marks </span>
			<span class="d-flex flex-column font-weight-bold text-white ma-3"> Choose a template to override the global marks for this station </span>
			<div class="d-flex flex-column ma-3">
				<v-select v-model="selectedStationID"
					:items="stationsMarkersList"
					item-title="stationName"
					item-value="stationID"
					base-color="white"
					variant="outlined"
					density="compact"
					hide-details
					:disabled="!assetManagementSelectedStation"
					style="color: white"
				>
					<template v-slot:append>
						<v-btn
							color="secondary"
							:disabled="!assetManagementSelectedStation"
							@click="handleOverrideStationMarkers">
							Override
						</v-btn>
					</template>
				</v-select>
			</div>
		</v-card>
	</div>
	<div v-else-if="hasAudio" class="container_row">
		<div class="d-inline-flex flex-column layer1">
            <span>
                {{ asset?.Title ?? "" }}
                {{
					asset.POIMarkup.Length ? asset.POIMarkup.Length :
					secondsToFormat(duration, "hh:mm:ss", {trim: "left", stopTrim: "m"})
				}}
            </span>
		</div>

		<div id="waveform-container" class="waveform-container layer2 fill-height">
			<Loader :value="!waveformLoaded" :height="45"/>
		</div>

		<div class="d-flex flex-row align-center justify-center my-4">
			<div class="d-flex flex-row">
				<v-btn class="mx-1" @click="zoomOut">
					<v-icon icon="mdi-magnify-minus-outline" @click="zoomOut"/>
				</v-btn>
				<v-btn class="mx-1" @click="zoomIn">
					<v-icon icon="mdi-magnify-plus-outline" @click="zoomIn"/>
				</v-btn>
			</div>
			<div class="d-flex flex-row " style="width: 8.33%">
				<v-text-field :model-value="secondsToFormat(currentTime, 'mm:ss')"
					variant="outlined"
					density="compact"
					bg-color="black"
					readonly
					hide-details/>
			</div>
			<div class="d-flex flex-row">
				<v-btn class="mx-1" size="40" @click="goToStart">
					<v-icon icon="mdi-skip-previous"/>
				</v-btn>
				<v-btn class="mx-1" size="40" @click="rewind">
					<v-icon icon="mdi-rewind"/>
				</v-btn>
				<v-btn class="mx-1" size="40" @click="handlePlayPause">
					<v-icon :icon="audioIsPlaying ? 'mdi-pause' : 'mdi-play'"/>
				</v-btn>
				<v-btn class="mx-1" size="40"
					:disabled="currentTime === 0"
					@click="stopAudioWaveform"
				>
					<v-icon icon="mdi-stop"/>
				</v-btn>
				<v-btn class="mx-1" size="40" @click="fastForward">
					<v-icon icon="mdi-fast-forward"/>
				</v-btn>
				<v-btn class="mx-1" size="40" @click="goToEnd">
					<v-icon icon="mdi-skip-forward"/>
				</v-btn>
			</div>
		</div>
	</div>
	<div v-else id="waveform-container"
		class="d-flex align-center justify-center w-100">
		<span class="text-caption" style="font-size: 1.2rem !important">
			Asset does not have audio!
		</span>
	</div>
</template>

<script>
/* Libraries */
import WaveSurfer from "wavesurfer.js";
import RegionsPlugin from "wavesurfer.js/dist/plugin/wavesurfer.regions.min";

/* Modules */
import Loader from "@/components/Loader.vue";

/* Mixins */
import AssetPropertiesMixin from "@/mixins/AssetProperties";
import DurationFormatMixin from "@/mixins/DurationFormatMixin";
import emitter from "@/emitter";

/* Enums */
import POITypes from "@/enums/POITypes";

const WAVEFORM_COLORS_ENUM = {
	"fadeIn": "#f44336",
	"fadeOut": "#f44336",
	"beforeFade": "rgba(255, 182, 193, 0.4)",
	"afterFade": "rgba(255, 182, 193, 0.4)",
	"intro": "rgb(255, 189, 0)",
	"segue": "#4caf50",
	"earlySegue": "#9c27b0"
}

export default {
	name: "WaveformWidget",

	components: {
		Loader
	},

	props: ["openSelection"],

	emits: ["update:marker", "update:region", "override:stationMarker"],

	mixins: [AssetPropertiesMixin, DurationFormatMixin],

	data() {
		return {
			isSelectionOpen: false,
			selectedStationID: "Global",
			currentTime: 0,
			waveform: null,
			waveformLoaded: false,
			audioMissing: false
		}
	},

	computed: {
		event() {
			return this.$store.getters.getAssetManagementEvent();
		},

		asset() {
			return this.$store.getters.getAssetManagementAsset();
		},

		assetManagementSelectedStation() {
			return this.$store.getters.getAssetManagementSelectedStation();
		},

		stationsMarkersList() {
			return this.$store.getters.getAssetManagementSelectedStationMarkersList();
		},

		selectedStationName() {
			return this.$store.getters.getAssetManagementSelectedStationName();
		},

		studioSettings() {
			return this.$store.getters.getStudioSettings();
		},

		audioIsPlaying() {
			return this.waveform?.isPlaying();
		},

		activeStudioAuditionOutputDevice() {
			return this.$store.getters.getActiveStudioAuditionOutputDevice();
		},

		darkMode() {
			return this.$store.getters.darkMode();
		}
	},

	methods: {
		getAuditionAudioURL() {
			if (!this.asset?.AuditionAudioUrl) {
				this.$store.dispatch("getAssetAudioUrl", this.asset)
					.then(response => {
						if (response.status === 200) {
							this.$nextTick(() => {
								this.initWaveform(response.data.url);
							});
						}
					})
					.catch(() => {
						this.$store.dispatch("getAssetPartialAudio", this.asset)
							.then(response => {
								this.$nextTick(() => {
									this.initWaveform(response.data);
								});
							})
							.catch(() => {
								this.$store.commit(
									"SHOW_NOTIFICATION_WITH_TYPE",
									{message: "An error occurred when trying to load the Asset."}
								);
							});
					});
			}
			else {
				this.$nextTick(() => {
					this.initWaveform(this.asset.AuditionAudioUrl);
				});
			}
		},

		initWaveform(audioSource) {
			if (this.waveform) {
				this.waveform.destroy();
			}
			const color = this.darkMode ? "#ffffff" : "#0a0b09";
			this.waveform = WaveSurfer.create({
				container: "#waveform-container",
				height: 440,
				plugins: [RegionsPlugin.create(),],
				waveColor: color,
				progressColor: color,
				cursorColor: color,
			});
			if (audioSource instanceof ArrayBuffer) {
				this.waveform.loadArrayBuffer(audioSource);
			}
			else {
				this.waveform.load(audioSource);
			}

			this.waveform.on("ready", () => {
				this.waveformLoaded = true;
				this.setWaveformRegions();
				this.setWaveformRegionsUpdateListeners();
			});

			this.waveform.on("play", () => this.applyPlaybackFades());

			this.waveform.on("seek", () => {
				this.currentTime = this.waveform.getCurrentTime();
				this.$emit("update:marker", this.currentTime);
			});

			this.waveform.on("audioprocess", (currentTime) => {
				this.currentTime = currentTime;
			})

			this.waveform.on("error", () => {
				this.$store.commit(
					"SHOW_NOTIFICATION_WITH_TYPE",
					{message: "Audio format not supported or source not found."}
				)
			});

			this.waveform.on("destroy", () => {
				this.waveform = null;
			});
		},

		setWaveformRegions() {
			this.waveform.addRegion({
				id: "fadeIn",
				start: this.fadeIn,
				end: this.fadeIn + this.fadeInLength,
				color: WAVEFORM_COLORS_ENUM.fadeIn,
				drag: true,
				resize: true
			});

			this.waveform.addRegion({
				id: "beforeFadeIn",
				start: 0,
				end: this.fadeIn,
				color: WAVEFORM_COLORS_ENUM.beforeFade,
				drag: false,
				resize: false
			});

			this.waveform.addRegion({
				id: "fadeOut",
				start: this.fadeOut ?? this.asset.POIMarkup.Length,
				end: this.fadeOut ? this.fadeOut + this.fadeOutLength : this.asset.POIMarkup.Length,
				color: WAVEFORM_COLORS_ENUM.fadeOut,
				drag: true,
				resize: true
			});

			this.waveform.addRegion({
				id: "afterFadeOut",
				start: this.fadeOut ? this.fadeOut + this.fadeOutLength : this.asset.POIMarkup.Length,
				end: this.asset.POIMarkup.Length,
				color: WAVEFORM_COLORS_ENUM.afterFade,
				drag: false,
				resize: false
			});

			this.getPoiMarkersByType(this.markers?.POIMarkers, POITypes.Intro).forEach((marker, index) => {
				this.waveform.addRegion({
					id: `intro${index > 0 ? index + 1 : ""}`,
					start: marker.Position,
					color: WAVEFORM_COLORS_ENUM.intro,
					drag: true,
					resize: false
				});
			});

			this.waveform.addRegion({
				id: "segue",
				start: this.segue,
				color: WAVEFORM_COLORS_ENUM.segue,
				drag: true,
				resize: false
			});

			this.waveform.addRegion({
				id: "earlySegue",
				start: this.earlySegue,
				color: WAVEFORM_COLORS_ENUM.earlySegue,
				drag: true,
				resize: false
			});
		},

		// initial update listeners setup
		setWaveformRegionsUpdateListeners() {
			Object.values(this.waveform.regions.list).forEach(region => {
				region.on("update", () => {
					let field = region.id;
					let value = this.waveform.regions.list[field].start;
					const {fadeIn, fadeOut} = this.waveform.regions.list;

					if (field === "fadeIn") {
						const fadeInEnd = fadeIn?.end;

						// move intro, segue and earlySegue
						["intro", "intro2", "intro3", "segue", "earlySegue"].forEach(key => {
							const region = this.waveform.regions.list?.[key];
							if (region && region.start < fadeInEnd) {
								region.update({
									start: fadeInEnd,
									end: fadeInEnd
								});
							}
						});

						//update section from start of waveform to start of fade in
						this.waveform.regions.list.beforeFadeIn.update({
							start: 0,
							end: value
						});

						// update fade in length
						this.$emit("update:region", {
							field: "fadeInLength",
							value: (fadeInEnd - fadeIn?.start) || 0
						});
					}
					else if (field === "fadeOut") {
						const fadeOutStart = fadeOut?.start;

						// move intro, segue and earlySegue
						["intro", "intro2", "intro3", "segue", "earlySegue"].forEach(key => {
							const region = this.waveform.regions.list?.[key];
							if (region && region.start > fadeOutStart) {
								region.update({
									start: fadeOutStart,
									end: fadeOutStart
								});
							}
						});

						//update section from end of fade out to end of waveform
						this.waveform.regions.list.afterFadeOut.update({
							start: fadeOut?.end,
							end: this.asset.POIMarkup.Length
						});

						// update fade out length
						this.$emit("update:region", {
							field: "fadeOutLength",
							value: (fadeOut?.end - fadeOutStart) || 0
						});
					}
					else {
						if (["intro", "intro2", "intro3", "segue", "earlySegue"].includes(field)) {
							value = this.applyRestrictions(value, fadeIn?.end || 0, fadeOut?.start || this.asset.POIMarkup.Length);
							this.waveform.regions.list[field].start = value;
							this.waveform.regions.list[field].end = value;
						}
					}
					this.$emit("update:region", {
						field,
						value
					});
				});
			});
		},

		// update listeners for fields created after initialization
		handleUpdateRegion(field, value = null) {
			if (!value) {
				value = this.waveform.regions.list[field]?.start || 0;
				if (["intro", "intro2", "intro3", "segue", "earlySegue"].includes(field)) {
					value = this.applyRestrictions(
						value,
						this.waveform.regions.list?.fadeIn?.end,
						this.waveform.regions.list?.fadeOut?.start
					);
					this.waveform.regions.list[field].start = value;
				}
			}

			this.$emit("update:region", {
				field,
				value
			});
			return {field, value};
		},

		handleUpdateMarker({field, value}) {
			if (field.includes("Length")) {
				let marker = Object.values(this.waveform.regions.list).find(r => r.id === field.split("Length")[0]);

				if (marker) {
					marker.update({
						end: marker.end + value,
					})
				}
			}
			else {
				let marker = Object.values(this.waveform.regions.list).find(r => r.id === field);
				const {fadeIn, fadeOut} = this.waveform.regions.list;
				if (value && marker) {
					value = this.applyRestrictions(value, fadeIn.end || 0, fadeOut.start || this.asset.POIMarkup.Length);
					marker.update({start: value, end: value});
					if (marker.id === "fadeIn") {
						if (this.waveform.regions.list.beforeFadeIn) {
							this.waveform.regions.list.beforeFadeIn.update({start: 0, end: value});
						}
						else {
							this.waveform.addRegion({
								id: "beforeFadeIn",
								start: 0,
								end: this.fadeIn,
								color: WAVEFORM_COLORS_ENUM.beforeFade,
								drag: false,
								resize: false
							});
						}
					}
					else if (marker.id === "fadeOut") {
						if (this.waveform.regions.list.afterFadeOut) {
							this.waveform.regions.list.afterFadeOut.update({
								start: this.waveform.regions.list.fadeOut.end,
								end: this.asset.POIMarkup.Length
							});
						}
						else {
							this.waveform.addRegion({
								id: "afterFadeOut",
								start: this.waveform.regions.list.fadeOut.end,
								end: this.asset.POIMarkup.Length,
								color: WAVEFORM_COLORS_ENUM.afterFade,
								drag: false,
								resize: false
							});
						}
					}
				}
				else if (marker) {
					marker.remove();

					if (marker.id === "fadeIn") {
						this.waveform.regions.list.beforeFadeIn.remove();
					}
					else if (marker.id === "fadeOut") {
						this.waveform.regions.list.afterFadeOut.remove();
					}
				}
				else if (value) {
					this.waveform.addRegion({
						id: field,
						start: value,
						end: value,
						color: WAVEFORM_COLORS_ENUM[field],
						drag: true,
						resize: field.includes("fade")
					});

					if (field === "fadeIn") {
						this.waveform.addRegion({
							id: "beforeFadeIn",
							start: 0,
							end: this.fadeIn,
							color: WAVEFORM_COLORS_ENUM.beforeFade,
							drag: false,
							resize: false
						});
						this.waveform.regions.list.fadeIn.on("update", () => {
							const fadeInStart = this.waveform.regions.list.fadeIn.start;
							const fadeInEnd = this.waveform.regions.list.fadeIn.end;
							this.handleUpdateRegion("fadeIn");
							this.handleUpdateRegion("fadeInLength", fadeInEnd - fadeInStart || 0);
						});
					}
					else if (field === "fadeOut") {
						this.waveform.addRegion({
							id: "afterFadeOut",
							start: this.fadeOut + this.fadeOutLength,
							end: this.asset.POIMarkup.Length,
							color: WAVEFORM_COLORS_ENUM.afterFade,
							drag: false,
							resize: false
						});
						this.waveform.regions.list["fadeOutLength"].on("update", () => {
								const fadeOutStart = this.waveform.regions.list.fadeOut.start;
								const fadeOutEnd = this.waveform.regions.list.fadeOut.end;
								this.handleUpdateRegion("fadeOut");
								this.handleUpdateRegion("fadeOutLength", (fadeOutEnd - fadeOutStart) || 0);
							}
						);
					}
					else {
						this.waveform.regions.list[field].on("update", () => this.handleUpdateRegion(field));
					}
				}
			}
		},

		handleUpdateCurrentMarker(value) {
			this.waveform.setCurrentTime(value);
		},

		handleAddIntro(field) {
			if (this.waveform) {
				this.waveform.addRegion({
					id: field,
					start: this.waveform.regions.list.fadeIn?.end || 0,
					end: this.waveform.regions.list.fadeIn?.end || 0,
					color: WAVEFORM_COLORS_ENUM.intro,
					drag: true,
					resize: false
				});

				this.waveform.regions.list[field].on("update", () => this.handleUpdateRegion(field));
			}
		},

		handlePlayPause() {
			this.audioIsPlaying ? this.pauseAudioWaveform() : this.playAudioWaveform();
		},

		playAudioWaveform() {
			this.setAudioOutDevice();
			this.waveform.play();
		},

		// Calculates and schedules Web Audio API volume automations based on Fade-In and Fade-Out POI markers.
		applyPlaybackFades() {
			const ac = this.waveform.backend.ac;
			const gainNode = this.waveform.backend.gainNode;
			const now = ac.currentTime;
			const currentCursorTime = this.waveform.getCurrentTime();

			const poiFadeIn = this.getLastPoiMarkerByType(POITypes.FadeIn);
			const poiFadeOut = this.getLastPoiMarkerByType(POITypes.FadeOut);

			// Clear all previous automation and set a baseline
			gainNode.gain.cancelScheduledValues(now);

			// Default to full volume unless a fade-in tells us otherwise
			let initialVolume = 1;

			// Fade-in logic
			if (poiFadeIn) {
				const fadeInEnd = poiFadeIn.Position + poiFadeIn.Duration;

				if (currentCursorTime < fadeInEnd) {
					initialVolume = 0; // Start silent if we are before or inside the fade-in

					const startDelay = Math.max(0, poiFadeIn.Position - currentCursorTime);
					const endDelay = fadeInEnd - currentCursorTime;

					const startTime = now + startDelay;
					const endTime = now + endDelay;

					// Anchor at 0, then ramp to 1
					gainNode.gain.setValueAtTime(0, startTime);
					gainNode.gain.linearRampToValueAtTime(1, endTime);
				}
			}

			// Set the starting volume for this exact moment
			// (If no fade-in is active, this remains 1)
			gainNode.gain.setValueAtTime(initialVolume, now);

			// Fade out-logic
			if (poiFadeOut) {
				const fadeOutEnd = poiFadeOut.Position + poiFadeOut.Duration;

				if (currentCursorTime < fadeOutEnd) {
					const startDelay = Math.max(0, poiFadeOut.Position - currentCursorTime);
					const endDelay = fadeOutEnd - currentCursorTime;

					const startTime = now + startDelay;
					const endTime = now + endDelay;

					// Anchor at 1 (or current volume) then ramp to 0
					// We use '1' here assuming the fade-in has completed by the time fade-out starts
					gainNode.gain.setValueAtTime(1, startTime);
					gainNode.gain.linearRampToValueAtTime(0, endTime);
				}
			}
		},

		pauseAudioWaveform() {
			this.waveform.pause();
		},

		stopAudioWaveform() {
			if (this.waveformLoaded) {
				this.waveform.stop();
			}
			else {
				this.waveform?.destroy();
			}
		},

		fastForward() {
			this.waveform.skip(5);
		},

		rewind() {
			this.waveform.skip(-5);
		},

		goToStart() {
			this.waveform.seekTo(0);
		},

		goToEnd() {
			let endMarker = (this.asset.POIMarkup.Length - 5) / this.asset.POIMarkup.Length;
			if (this.segue) {
				endMarker = (this.segue - 5) / this.asset.POIMarkup.Length;
			}
			else if (this.fadeOut) {
				endMarker = (this.fadeOut - 5) / this.asset.POIMarkup.Length;
			}
			this.waveform.seekTo(endMarker);
		},

		zoomIn() {
			this.waveform.zoom(20);
		},

		zoomOut() {
			this.waveform.zoom(-20);
		},

		setAudioOutDevice() {
			this.waveform.setSinkId(this.activeStudioAuditionOutputDevice?.device ?? "default");
		},

		handleOverrideStationMarkers() {
			this.isSelectionOpen = false;
			if (this.selectedStationID !== "Global" && this.selectedStationID !==
				this.assetManagementSelectedStation?.StationID) {
				this.$emit("override:stationMarker", this.selectedStationID);
			}
			else {
				this.waveformLoaded = false;
				this.getAuditionAudioURL();
			}
		},

		applyRestrictions(value, min, max) {
			return Math.max(min, Math.min(max, value)) || value;
		}
	},

	watch: {
		asset(asset, prevAsset) {
			if (asset && asset?.AssetID !== prevAsset?.AssetID) {
				this.getAuditionAudioURL();
			}
		},

		selectedStationMarkers() {
			this.waveformLoaded = false;
			this.getAuditionAudioURL();
		},

		activeStudioAuditionOutputDevice() {
			if (this.waveform) {
				this.setAudioOutDevice();
			}
		},

		hasAudio() {
			if (this.waveform && !this.hasAudio) {
				this.waveform.destroy();
			}
			else {
				this.getAuditionAudioURL();
			}
		},

		darkMode(isDarkMode) {
			const color = isDarkMode ? "#ffffff" : "#0a0b09";
			this.waveform?.setWaveColor(color);
			this.waveform?.setProgressColor(color);
			this.waveform?.setCursorColor(color);
		}
	},

	mounted() {
		if (this.openSelection) {
			this.isSelectionOpen = true;
		}
		else if (this.asset && this.hasAudio) {
			this.getAuditionAudioURL();
		}

		emitter.on("update:marker", this.handleUpdateMarker);
		emitter.on("update:currentMarker", this.handleUpdateCurrentMarker);
		emitter.on("addIntro", this.handleAddIntro);
	},

	beforeUnmount() {
		if (this.waveform) {
			this.stopAudioWaveform();
		}
		emitter.off("update:marker", this.handleUpdateMarker);
		emitter.off("update:currentMarker", this.handleUpdateCurrentMarker);
		emitter.off("addIntro", this.handleAddIntro);
	}
}
</script>

<style scoped lang="scss">
:deep(.waveform-container) {
	position: relative;

	.wavesurfer-region {
		min-width: 3px;
		opacity: 0.5;

		&[data-id="fadeIn"] {
			> .wavesurfer-handle {
				//width: 0px !important;
				background-color: rgb(255, 0, 0) !important;
				opacity: 1;
				border: none;
			}
		}

		&[data-id="fadeOut"] {
			> .wavesurfer-handle {
				background-color: rgb(255, 0, 0) !important;
				opacity: 1;
				border: none;
			}
		}
	}

	.container_row {
		display: grid;
	}

	.layer1, .layer2 {
		grid-column: 1;
		grid-row: 1;
	}

	.layer1 {
		//z-index: 4;

		span {
			font-size: 12px;
			line-height: 16px;
			color: #717277;
		}
	}

	.asset-missing {
		color: #c6002b !important;
		font-weight: 600;
	}
}

</style>
